/**
 * API Client for Web Dashboard
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

export interface UserOrganization {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    username: string;
    role: 'admin' | 'user';
    organizations: UserOrganization[];
  };
}

export interface User {
  id: string;
  username: string;
  role: 'admin' | 'user';
  isActive: boolean;
  createdAt: string;
}

export interface Receipt {
  id: string;
  userId: string;
  categoryId: string | null;
  total: number;
  currency: string;
  date: string;
  vendor: string | null;
  items: string | null;
  notes: string | null;
  imageUrl: string;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  tags?: string[];
}

export interface Category {
  id: string;
  name: string;
  createdAt: string;
}

export interface Tag {
  id: string;
  name: string;
  createdAt: string;
}

export interface AISettings {
  provider: string;
  geminiModel: string;
  openrouterModel: string;
  vertexModel: string;
  geminiApiKey: string;
  openrouterApiKey: string;
  vertexApiKey: string;
  hasGeminiKey: boolean;
  hasOpenrouterKey: boolean;
  hasVertexKey: boolean;
}

export interface ReceiptListResponse {
  receipts: Receipt[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserListResponse {
  users: User[];
}

// Income types
export interface IncomeCategory {
  id: string;
  name: string;
  isSystem: boolean;
  createdAt: string;
}

export interface Income {
  id: string;
  userId: string;
  categoryId: string | null;
  categoryName: string | null;
  amount: number;
  currency: string;
  date: string;
  description: string;
  notes: string | null;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  username: string | null;
}

export interface IncomeListResponse {
  income: Income[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface IncomeAuditLog {
  id: string;
  incomeId: string;
  userId: string;
  action: 'create' | 'update' | 'delete' | 'restore';
  changes: string | null;
  createdAt: string;
  username: string | null;
  description: string | null;
}

export interface IncomeAuditListResponse {
  auditLogs: IncomeAuditLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Report types
export interface IncomeSummary {
  totalIncome: number;
  count: number;
  averageIncome: number;
}

export interface CategoryReportItem {
  categoryId: string | null;
  categoryName: string | null;
  totalAmount: number;
  count: number;
}

export interface MonthlyReportItem {
  month: string;
  totalAmount: number;
  count: number;
}

export interface UserReportItem {
  userId: string;
  username: string | null;
  totalAmount: number;
  count: number;
}

export interface FinancialSummary {
  income: {
    total: number;
    count: number;
    average: number;
  };
  expenses: {
    total: number;
    count: number;
    average: number;
  };
  net: number;
  savingsRate: number;
  status: 'surplus' | 'deficit';
}

// Organization types
export interface Organization {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationMember {
  userId: string;
  username: string;
  role: 'owner' | 'admin' | 'member';
  createdAt: string;
}

export interface OrganizationListResponse {
  organizations: Organization[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface OrganizationMembersResponse {
  members: OrganizationMember[];
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private organizationId: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  setOrganizationId(organizationId: string | null) {
    this.organizationId = organizationId;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.organizationId) {
      headers['X-Organization-Id'] = this.organizationId;
    }

    try {
      const response = await fetch(`${this.baseUrl}/v1${endpoint}`, {
        ...options,
        headers: { ...headers, ...options.headers },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || {
            code: 'UNKNOWN_ERROR',
            message: 'An unexpected error occurred',
          },
        };
      }

      return data as ApiResponse<T>;
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  // Auth
  async login(username: string, password: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  // Users
  async getUsers(search?: string): Promise<ApiResponse<UserListResponse>> {
    const queryParams = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<UserListResponse>(`/users${queryParams}`);
  }

  async createUser(data: {
    username: string;
    password: string;
    role: 'admin' | 'user';
  }): Promise<ApiResponse<User>> {
    return this.request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(
    id: string,
    data: { role?: 'admin' | 'user'; isActive?: boolean }
  ): Promise<ApiResponse<User>> {
    return this.request<User>(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async resetUserPassword(
    id: string,
    newPassword: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  }

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // Receipts
  async getReceipts(params?: {
    page?: number;
    limit?: number;
    category?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<ApiResponse<ReceiptListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    return this.request<ReceiptListResponse>(
      `/receipts${queryString ? `?${queryString}` : ''}`
    );
  }

  async getReceipt(id: string): Promise<ApiResponse<Receipt>> {
    return this.request<Receipt>(`/receipts/${id}`);
  }

  async deleteReceipt(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/receipts/${id}`, {
      method: 'DELETE',
    });
  }

  // Categories
  async getCategories(): Promise<ApiResponse<{ categories: Category[] }>> {
    return this.request<{ categories: Category[] }>('/categories');
  }

  async createCategory(name: string): Promise<ApiResponse<Category>> {
    return this.request<Category>('/categories', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateCategory(id: string, name: string): Promise<ApiResponse<Category>> {
    return this.request<Category>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteCategory(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Tags
  async getTags(): Promise<ApiResponse<{ tags: Tag[] }>> {
    return this.request<{ tags: Tag[] }>('/tags');
  }

  async createTag(name: string): Promise<ApiResponse<Tag>> {
    return this.request<Tag>('/tags', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateTag(id: string, name: string): Promise<ApiResponse<Tag>> {
    return this.request<Tag>(`/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteTag(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/tags/${id}`, {
      method: 'DELETE',
    });
  }

  // Settings
  async getSettings(): Promise<ApiResponse<{ settings: Record<string, string> }>> {
    return this.request<{ settings: Record<string, string> }>('/settings');
  }

  async getAISettings(): Promise<ApiResponse<AISettings>> {
    return this.request<AISettings>('/settings/ai');
  }

  async updateAISettings(data: {
    provider?: string;
    geminiModel?: string;
    openrouterModel?: string;
    vertexModel?: string;
    geminiApiKey?: string;
    openrouterApiKey?: string;
    vertexApiKey?: string;
  }): Promise<ApiResponse<AISettings>> {
    return this.request<AISettings>('/settings/ai', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getSetting(key: string): Promise<ApiResponse<{ key: string; value: string }>> {
    return this.request<{ key: string; value: string }>(`/settings/${key}`);
  }

  async setSetting(key: string, value: string): Promise<ApiResponse<{ key: string; value: string }>> {
    return this.request<{ key: string; value: string }>(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  async deleteSetting(key: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/settings/${key}`, {
      method: 'DELETE',
    });
  }

  // Income Categories
  async getIncomeCategories(): Promise<ApiResponse<{ categories: IncomeCategory[] }>> {
    return this.request<{ categories: IncomeCategory[] }>('/income-categories');
  }

  async createIncomeCategory(name: string): Promise<ApiResponse<IncomeCategory>> {
    return this.request<IncomeCategory>('/income-categories', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateIncomeCategory(id: string, name: string): Promise<ApiResponse<IncomeCategory>> {
    return this.request<IncomeCategory>(`/income-categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  async deleteIncomeCategory(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/income-categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Income
  async getIncomes(params?: {
    page?: number;
    limit?: number;
    category?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<ApiResponse<IncomeListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    return this.request<IncomeListResponse>(
      `/income${queryString ? `?${queryString}` : ''}`
    );
  }

  async getIncome(id: string): Promise<ApiResponse<Income>> {
    return this.request<Income>(`/income/${id}`);
  }

  async createIncome(data: {
    amount: number;
    currency?: string;
    date: string;
    description: string;
    categoryId?: string | null;
    notes?: string | null;
    userId?: string;
  }): Promise<ApiResponse<Income>> {
    return this.request<Income>('/income', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateIncome(
    id: string,
    data: {
      amount?: number;
      currency?: string;
      date?: string;
      description?: string;
      categoryId?: string | null;
      notes?: string | null;
    userId?: string;
    }
  ): Promise<ApiResponse<Income>> {
    return this.request<Income>(`/income/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteIncome(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/income/${id}`, {
      method: 'DELETE',
    });
  }

  async permanentDeleteIncome(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/income/${id}/permanent`, {
      method: 'DELETE',
    });
  }

  async restoreIncome(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/income/${id}/restore`, {
      method: 'POST',
    });
  }

  // Income Audit
  async getIncomeAuditLogs(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<IncomeAuditListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request<IncomeAuditListResponse>(
      `/income-audit${queryString ? `?${queryString}` : ''}`
    );
  }

  // Reports
  async getIncomeSummary(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<IncomeSummary>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request<IncomeSummary>(
      `/reports/income-summary${queryString ? `?${queryString}` : ''}`
    );
  }

  async getIncomeByCategory(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{ data: CategoryReportItem[] }>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request<{ data: CategoryReportItem[] }>(
      `/reports/income-by-category${queryString ? `?${queryString}` : ''}`
    );
  }

  async getIncomeByMonth(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{ data: MonthlyReportItem[] }>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request<{ data: MonthlyReportItem[] }>(
      `/reports/income-by-month${queryString ? `?${queryString}` : ''}`
    );
  }

  async getIncomeByUser(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{ data: UserReportItem[] }>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request<{ data: UserReportItem[] }>(
      `/reports/income-by-user${queryString ? `?${queryString}` : ''}`
    );
  }

  async getFinancialSummary(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<FinancialSummary>> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const queryString = queryParams.toString();
    return this.request<FinancialSummary>(
      `/reports/financial-summary${queryString ? `?${queryString}` : ''}`
    );
  }

  // Organizations
  async getOrganizations(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ApiResponse<OrganizationListResponse>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    return this.request<OrganizationListResponse>(
      `/organizations${queryString ? `?${queryString}` : ''}`
    );
  }

  async getOrganization(id: string): Promise<ApiResponse<Organization>> {
    return this.request<Organization>(`/organizations/${id}`);
  }

  async createOrganization(data: {
    name: string;
    description?: string;
  }): Promise<ApiResponse<Organization>> {
    return this.request<Organization>('/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOrganization(
    id: string,
    data: { name?: string; description?: string; isActive?: boolean }
  ): Promise<ApiResponse<Organization>> {
    return this.request<Organization>(`/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(id: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/organizations/${id}`, {
      method: 'DELETE',
    });
  }

  async reactivateOrganization(id: string): Promise<ApiResponse<Organization>> {
    return this.request<Organization>(`/organizations/${id}/reactivate`, {
      method: 'POST',
    });
  }

  // Organization Members
  async getOrganizationMembers(orgId: string): Promise<ApiResponse<OrganizationMembersResponse>> {
    return this.request<OrganizationMembersResponse>(`/organizations/${orgId}/members`);
  }

  async addOrganizationMember(
    orgId: string,
    data: { userId: string; role: 'admin' | 'member' }
  ): Promise<ApiResponse<OrganizationMember>> {
    return this.request<OrganizationMember>(`/organizations/${orgId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOrganizationMemberRole(
    orgId: string,
    userId: string,
    role: 'admin' | 'member'
  ): Promise<ApiResponse<OrganizationMember>> {
    return this.request<OrganizationMember>(`/organizations/${orgId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeOrganizationMember(
    orgId: string,
    userId: string
  ): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/organizations/${orgId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  // Create user for organization (org admin feature)
  async createOrganizationUser(
    orgId: string,
    data: {
      username: string;
      email?: string;
      password: string;
      displayName?: string;
      role: 'member' | 'admin';
    }
  ): Promise<ApiResponse<{
    user: { id: string; username: string; displayName: string };
    membership: { organizationId: string; role: string };
    message: string;
  }>> {
    return this.request(`/organizations/${orgId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Invite Management
  async createInvite(
    orgId: string,
    data: {
      expiresIn: '24h' | '7d' | '30d' | 'never';
      maxUses: number | null;
      defaultRole: 'member' | 'admin';
    }
  ): Promise<ApiResponse<{
    id: string;
    token: string;
    link: string;
    expiresAt: string | null;
    maxUses: number | null;
    defaultRole: string;
    message: string;
  }>> {
    return this.request(`/organizations/${orgId}/invites`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInvites(orgId: string): Promise<ApiResponse<{
    invites: Array<{
      id: string;
      token: string;
      link: string;
      defaultRole: string;
      maxUses: number | null;
      useCount: number;
      expiresAt: string | null;
      isExpired: boolean;
      isRevoked: boolean;
      isMaxUsesReached: boolean;
      createdAt: string;
      createdBy: { id: string; username: string };
    }>;
  }>> {
    return this.request(`/organizations/${orgId}/invites`);
  }

  async revokeInvite(orgId: string, inviteId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request(`/organizations/${orgId}/invites/${inviteId}`, {
      method: 'DELETE',
    });
  }

  // Public invite validation (no auth required)
  async validateInvite(token: string): Promise<ApiResponse<{
    valid: boolean;
    reason?: string;
    message?: string;
    organization?: { id: string; name: string };
    defaultRole?: string;
  }>> {
    return this.request(`/invites/${token}/validate`);
  }

  async registerWithInvite(
    token: string,
    data: {
      username: string;
      email?: string;
      password: string;
      displayName?: string;
    }
  ): Promise<ApiResponse<{
    user: { id: string; username: string };
    organization: { id: string; name: string };
    message: string;
  }>> {
    return this.request(`/invites/${token}/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const api = new ApiClient(API_BASE_URL);
